import React, { useState, useEffect } from 'react';
import { footerLinkService } from '../../services/api';
import { Plus, Pencil, Trash2, X, Link2, Layers } from 'lucide-react';

function FooterLinksManagement() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ column_title: '', label: '', url: '', column_index: 0, sort_order: 0, is_active: true });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { loadLinks(); }, []);

  const loadLinks = async () => {
    setLoading(true);
    try { setLinks(await footerLinkService.index()); } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await footerLinkService.update(editingId, form);
      else await footerLinkService.store(form);
      setShowForm(false); setEditingId(null); setForm({ column_title: '', label: '', url: '', column_index: 0, sort_order: 0, is_active: true });
      loadLinks();
    } catch (error) { alert('Error saving link'); }
  };

  const handleEdit = (l) => { setForm({ ...l }); setEditingId(l.id); setShowForm(true); };
  const handleDelete = async (id) => { if (!window.confirm('Delete this link?')) return; try { await footerLinkService.destroy(id); loadLinks(); } catch (e) { alert('Error'); } };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  const grouped = links.reduce((acc, l) => {
    const col = l.column_title || 'Other';
    if (!acc[col]) acc[col] = [];
    acc[col].push(l);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-black text-white">Footer Links</h2>
          <p className="text-slate-500 text-sm mt-1">Manage navigation links displayed in the website footer</p>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingId(null); setForm({ column_title: '', label: '', url: '', column_index: 0, sort_order: 0, is_active: true }); }}
          className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-red-700 transition shadow-lg"
        >
          <Plus size={18} /> Add Link
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 max-w-lg w-full space-y-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-lg text-white flex items-center gap-2">
                <Link2 size={20} className="text-red-500" />
                {editingId ? 'Edit' : 'New'} Footer Link
              </h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-slate-500 hover:text-white transition">
                <X size={20} />
              </button>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Column Title</label>
              <input required placeholder="e.g. About Us" value={form.column_title} onChange={e => setForm({ ...form, column_title: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-red-500 transition" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Label</label>
              <input required placeholder="e.g. Who We Are" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-red-500 transition" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">URL</label>
              <input required placeholder="/about or https://..." value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-red-500 transition" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Column Index</label>
                <input type="number" placeholder="0, 1, 2, 3" value={form.column_index} onChange={e => setForm({ ...form, column_index: parseInt(e.target.value) || 0 })} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-red-500 transition" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Sort Order</label>
                <input type="number" placeholder="0" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-red-500 transition" />
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="w-5 h-5 rounded accent-red-600" />
              <span className="text-sm text-slate-300 font-medium">Active</span>
            </label>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="flex-1 bg-red-600 text-white px-5 py-3 rounded-xl font-bold text-sm hover:bg-red-700 transition">{editingId ? 'Update Link' : 'Save Link'}</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-3 rounded-xl border border-slate-800 text-slate-400 font-bold text-sm hover:bg-slate-800 transition">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Grouped cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(grouped).map(([colName, colLinks]) => (
          <div key={colName} className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
              <Layers size={16} className="text-red-500" />
              <h3 className="text-white font-bold text-sm uppercase tracking-wider">{colName}</h3>
              <span className="ml-auto text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">{colLinks.length}</span>
            </div>
            <div className="divide-y divide-slate-800/50">
              {colLinks.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map((l) => (
                <div key={l.id} className="px-5 py-3 flex items-center justify-between group hover:bg-slate-800/30 transition">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-200 truncate">{l.label}</p>
                    <p className="text-xs text-slate-500 truncate">{l.url}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${l.is_active ? 'bg-red-500/10 text-red-400' : 'bg-slate-800 text-slate-500'}`}>
                      {l.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button onClick={() => handleEdit(l)} className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleDelete(l.id)} className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-600/10 transition">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {links.length === 0 && (
        <div className="text-center py-20 bg-slate-900 border border-slate-800 rounded-2xl">
          <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center mx-auto mb-4">
            <Link2 size={28} className="text-slate-600" />
          </div>
          <h3 className="text-white font-bold mb-1">No Footer Links Yet</h3>
          <p className="text-slate-500 text-sm">Click "Add Link" to create your first footer link.</p>
        </div>
      )}
    </div>
  );
}

export default FooterLinksManagement;
